<template>
  <layout :title="$helper.testTitle" :sub-title="$test.fullLabel">
    <template slot="right-column">
      <help-button />
    </template>
    <form slot="container" v-loading="`<i class='ti ti-refresh ti-spin'></i>`" @submit.prevent>
      <template v-if="!isLoading">
        <test-content
          :test-info="testInfo"
          :previous-question="previousQuestion"
          :previous-question-module-label="previousQuestionModuleLabel"
          :question="question"
          :module-localised-label="moduleLocalisedLabel"
          :show-answer="showAnswer"
          :correction="correction"
        />
      </template>
      <loader-text v-else :title="$t('test.is_loading')" />
      <div v-if="!firstLoading" class="layout-footer question-footer border-top">
        <div class="d-flex h-100">
          <div class="actions h-100">
            <button-with-icon
              :text="isQuitPlayer ? $t('test.quit') : $t('test.stop')"
              variant="danger"
              :icon="isQuitPlayer ? 'ti-stop' : 'pause'"
              :is-loading="isLoading"
              @click="onQuitTestClick"
            />
          </div>
          <div class="d-flex align-items-center h-100 w-50">
            <div class="d-flex align-items-center h-100 w-40 border-right justify-content-around">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span class="progress-text" v-html="$t(`test.${$helper.isPlayerPrepa ? 'time_spent' : 'time_progress'}`)" />
              <progress-circle v-if="!$helper.isPlayerPrepa" :percent="getPercentTimeLeft" :fill="{ color: '#EBA614' }" />
              <span class="big-text time-left">
                {{ getTime }}
              </span>
            </div>
            <div class="number-bar d-flex align-items-center justify-content-between h-100 w-60 p-2">
              <div class="questions-progress d-flex align-items-center justify-content-center">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span class="progress-text" v-html="$t('test.progress')" />
                <progress-circle :percent="_.get(testInfo, 'percent', 0)" />
              </div>
              <span v-if="testInfo" class="big-text question-left">
                {{ getProgression }}
              </span>
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-if="testInfo && testInfo.nb_postpone > 0"
                class="postpone-question text-center"
                v-html="$t('test.postpone_question', { nb: testInfo.nb_postpone })"
              />
              <!-- eslint-enable vue/no-v-html -->
            </div>
          </div>
          <div class="actions h-100 border-right">
            <button-with-icon
              v-if="!$helper.isPlayerPrepa"
              :text="$t('test.report_question')"
              variant="warning"
              icon="ti-refresh"
              :is-loading="isLoading"
              @click="onPostponeClick"
            />
          </div>
          <div class="w-25 h-100">
            <template v-if="showAnswer">
              <b-button
                variant="success"
                class="h-100 text-uppercase"
                :disabled="isLoading"
                block
                @click="onShowNextQuestionClick"
              >
                <template v-if="!isLoading">
                  {{ $t(`test.${finished ? 'show_results' : 'next_question'}`) }}
                </template>
                <i v-else class="ti ti-refresh ti-spin" />
              </b-button>
            </template>
            <template v-else>
              <b-button
                v-if="answered"
                variant="success"
                class="h-100 text-uppercase"
                :disabled="isLoading"
                block
                type="submit"
                name="validate"
                @click="onValidateClick"
              >
                <template v-if="!isLoading">
                  {{ $t('test.validate') }}
                </template>
                <i v-else class="ti ti-refresh ti-spin" />
              </b-button>
              <b-button
                v-else
                variant="light"
                class="h-100 text-uppercase"
                :disabled="isLoading"
                block
                type="submit"
                name="noAnswer"
                @click="onNoAnswerClick"
              >
                <template v-if="!isLoading">
                  {{ $t('test.no_answered') }}
                </template>
                <i v-else class="ti ti-refresh ti-spin" />
              </b-button>
            </template>
          </div>
        </div>
      </div>
      <simplert ref="simplert" use-radius use-icon />
    </form>
  </layout>
</template>

<script>
  import Simplert from 'vue2-simplert';

  import Layout from '@/views/Layout';
  import TestContent from '@/components/test-content';
  import LoaderText from '@/components/loader-text';
  import ButtonWithIcon from '@/components/button-with-icon';
  import HelpButton from '@/components/help-button';
  import ProgressCircle from '@/components/progress-circle';
  import LoadOnNavigate from '@/mixins/load-on-navigate';

  export default {
    name: 'TestView',
    components: {
      Layout,
      TestContent,
      LoaderText,
      ButtonWithIcon,
      HelpButton,
      ProgressCircle,
      Simplert,
    },
    mixins: [
      LoadOnNavigate,
    ],
    data() {
      return {
        firstLoading: true,
        isLoading: true,
        inProgress: false,
        previousQuestion: null,
        showAnswer: false,
        question: null,
        action: null,
        answer: null,
        correction: null,
        previousQuestionModuleLabel: null,
        answered: false,
        testInfo: null,
        timerPercent: null,
        time: 0,
        finished: false,
        moduleLocalisedLabel: null,
      };
    },
    computed: {
      isQuitPlayer() {
        return this.$helper.isPlayerCertif || this.$helper.isPlayerDemo36 || this.$helper.isPlayerDemo12;
      },
      getProgression() {
        const { validated, total } = this.testInfo;

        return `${validated === total ? validated : validated + 1} / ${total}`;
      },
      getHumanTime() {
        if (this.time) {
          return this.$moment.duration(this.time, 'seconds').asSeconds();
        }
        return 0;
      },
      getTime() {
        return this.$moment.duration(Math.max(0, this.getHumanTime), 'seconds').format(this.formatTime(this.getHumanTime));
      },
      getPercentTimeLeft() {
        const totalTime = this.$test.data.total_time;
        return (totalTime - this.getHumanTime) * 100 / totalTime;
      },
    },
    mounted() {
      this.$bus.$on('model-updated', this.onAnswerKindModelUpdated);
    },
    destroyed() {
      this.$bus.$off(['model-updated']);
    },
    methods: {
      async load() {
        this.isLoading = true;
        const data = await this.getQuestion();
        if (data.finished) {
          this.finished = true;
          if (data.answer) {
            this.firstLoading = false;
            this.isLoading = false;
          } else {
            this.$router.push({ name: 'result' });
          }
        } else {
          this.now = Number(this.$moment());
          this.updateTime();
          this.firstLoading = false;
          this.isLoading = false;
        }
      },
      formatTime(time) {
        let format = '00:ss';

        if (time >= 3600) {
          format = 'hh:mm:ss';
        } else if (time >= 60) {
          format = 'mm:ss';
        }

        return format;
      },
      updateTime() {
        if (this.time >= 0) {
          const now = Number(this.$moment());
          this.time += (now - this.now) / 1000 * (this.$helper.isPlayerPrepa ? 1 : -1);
          this.now = now;
          setTimeout(this.updateTime, 1000);
          return;
        }
        if (!this.$helper.isPlayerPrepa) {
          this.$test.getTimeOut().then(() => {
            this.$router.push({ name: 'result' });
          });
        }
      },
      async getQuestion() {
        const data = await this.$test.getQuestion();
        this.formatQuestion(data);
        return data;
      },
      onShowNextQuestionClick() {
        this.isLoading = true;
        this.answered = false;

        if (this.nextTestInfo) {
          this.testInfo = this.nextTestInfo;
        }

        if (!this.finished) {
          this.showAnswer = false;
        } else {
          this.$router.push({ name: 'result' });
        }
        this.$nextTick(() => { this.isLoading = false; });
      },
      onAnswerKindModelUpdated(model) {
        this.answer = model;
        this.answered = true;
      },
      onNoAnswerClick() {
        this.action = 'noAnswer';
        return this.openAlert(this.$t('alert.no_answer.title'), this.$t('alert.no_answer.message'), this.$t('alert.no_answer.confirm'), this.postTest);
      },
      onPostponeClick() {
        this.action = 'postpone';
        return this.openAlert(this.$t('alert.postpone_test.title'), this.$t('alert.postpone_test.message'), this.$t('alert.postpone_test.confirm'), this.postTest);
      },
      onValidateClick() {
        this.action = 'validate';
        return this.postTest();
      },
      async postTest() {
        if (!this.inProgress) {
          this.inProgress = true;

          this.isLoading = true;
          const data = await this.$test.postTest(this.action, this._.transform(this.answer, (result, value, key) => {
            result[key] = key === 'answer' ? this._.castArray(value) : value;
            return result;
          }));
          this.isLoading = false;
          this.answered = false;

          if (data.finished) {
            this.finished = true;
          }

          if (data.finished && !data.answer) {
            this.$router.push({ name: 'result' });
          } else {
            this.formatQuestion(data);
          }

          this.inProgress = false;
        }
      },
      onQuitTestClick() {
        let message = this.$t('alert.quit_test.message');
        if (this.isQuitPlayer) {
          message = this.$t('alert.certif.quit_test.message');
        }

        this.openAlert(this.$t('alert.quit_test.title'), message, this.$t('alert.quit_test.confirm'), this.confirmClickOnQuitTest);
      },
      openAlert(title, message, customConfirmBtnText, onConfirm) {
        return new Promise((resolve) => {
          const alert = {
            title,
            message,
            type: 'info',
            useConfirmBtn: true,
            onConfirm: () => {
              resolve(onConfirm());
            },
            customConfirmBtnText,
            customCloseBtnText: this.$t('actions.close'),
            customCloseBtnClass: 'btn btn-default',
          };

          this.$refs.simplert.openSimplert(alert);
        });
      },
      confirmClickOnQuitTest() {
        this.isLoading = true;
        this.$test.quitTest();
      },
      formatQuestion(data) {
        if (this.$helper.isPlayerPrepa) {
          if (data.previous_question) {
            this.previousQuestion = data.previous_question;
          }

          if (data.previous_question_module_label) {
            this.previousQuestionModuleLabel = data.previous_question_module_label;
          }

          if (data.answer) {
            this.correction = data.answer;
            this.showAnswer = true;
          }
        }

        this.question = data.question;

        if (!this.testInfo || !this.$helper.isPlayerPrepa) {
          this.testInfo = data.test_information;
        } else {
          this.nextTestInfo = data.test_information;
        }

        if (this.time === 0) {
          this.time = this._.reduce(this._.pick(data.test_information, 'time_spent', 'time_left'), (carry, value) => value);
        }

        this.moduleLocalisedLabel = data.module_localised_label;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@/scss/vars';

  .w-20 {
    width: 20%;
  }

  .w-40 {
    width: 40%;
  }

  .w-60 {
    width: 60%;
  }

  .op-6 {
    opacity: 0.6;
  }

  .question-footer {
    .big-text {
      font-size: 1.8rem;
      font-weight: bold;
      min-width: 90px;
      text-align: center;

      &.time-left {
        color: $yellow;
      }

      &.question-left {
        color: $primary;
        margin-right: 0.5rem;
      }
    }

    .number-bar {
      > div,
      > span {
        flex: 1;

        &.questions-progress {
          flex: 2;
        }
      }
    }

    .btn-light {
      border-color: $grey;
      background-color: $grey;
      color: $white;

      &:hover {
        border-color: $grey;
        color: $white;
        background-color: darken($grey, 10);
      }
    }

    .progress-text {
      font-weight: bold;
      font-size: 70%;
      margin-right: 0.5rem;
      text-align: right;
    }

    .postpone-question {
      color: $primary;
      font-size: 60%;
      font-weight: bold;
      min-width: 150px;
    }

    .nb-question {
      width: $button-width * 3;
    }

    .actions {
      width: $button-width;

      .btn {
        color: $white !important;
      }
    }

    .btn {
      font-size: 14px;
    }
  }
</style>
